import { memo } from 'react'
import { format } from 'date-fns'
import { AudioLines } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { useAudioIndicatorTone } from '@renderer/hooks/audio/use-audio-indicator-tone'
import { AudioWaveform } from '@renderer/overlay/components/audio-waveform'
import { LiveTranscriptSnippet } from '@renderer/overlay/Sidepanel/components/meeting/live-transcript-snippet'
import type { LiveTranscriptSendKind } from '../../../../utils/audioNotes'
import { FadingScrollable } from '../../../elements/FadingScrollable'
import { PreviewableContext } from '../../input/context/previewable-context'
import { AttachmentStub } from '../attachment-stub'
import { scrollableAreaClassName } from '../styles'

const SENT_LABEL: Record<LiveTranscriptSendKind, string> = {
  full: 'Live transcript',
  update: 'Transcript update',
  unchanged: 'No new transcript',
}

const SENT_DESCRIPTION: Record<LiveTranscriptSendKind, string> = {
  full: 'The meeting so far, sent with this message.',
  update: 'What was said since the previous message.',
  unchanged: 'Nothing new had been said since the previous message.',
}

/**
 * The recording meeting on the composer: it reads as live, in the recording's
 * green while capturing, because each send takes whatever has been said by then.
 */
export const LiveTranscriptChip = memo(
  ({ title, startTime, onRemove }: { title: string; startTime: string; onRemove?: () => void }) => {
    const tone = useAudioIndicatorTone()

    return (
      <PreviewableContext
        preview={
          <div className="flex w-64 flex-col gap-2 p-4 pt-3.5">
            <div className="flex flex-col gap-0.5">
              <span className="type-ui-sm text-foreground truncate font-medium">{title}</span>
              <span className="type-ui-xs text-foreground-secondary">
                Recording · started {format(new Date(startTime), 'h:mmaaa')}
              </span>
            </div>
            <LiveTranscriptSnippet className="w-full" />
            <p className="type-ui-xs text-foreground-placeholder">
              Each message shares what&rsquo;s been said since your last one.
            </p>
          </div>
        }
      >
        <AttachmentStub
          label="Live transcript"
          icon={<AudioWaveform className="h-4" />}
          onRemove={onRemove}
          className={twMerge(
            'max-w-40',
            tone === 'live' &&
              'bg-success-subtle hover:bg-success-subtle-hover text-success-subtle hover:text-success-subtle',
          )}
        />
      </PreviewableContext>
    )
  },
)
LiveTranscriptChip.displayName = 'LiveTranscriptChip'

/**
 * A live meeting chip on a sent message, named for what that send carried.
 * `kind` is unknown until the message settles, while it still holds the
 * composer's chip.
 */
export const SentLiveTranscript = memo(
  ({ kind, transcript }: { kind?: LiveTranscriptSendKind; transcript?: string }) => {
    const pill = (
      <AttachmentStub
        label={SENT_LABEL[kind ?? 'full']}
        icon={<AudioLines size={16} className="text-success-subtle" />}
        className="max-w-40"
      />
    )
    if (!kind) return pill

    return (
      <PreviewableContext
        preview={
          <div className={twMerge('flex max-h-48 w-64 flex-col gap-2 p-4 pt-3.5', transcript && 'pb-0')}>
            <div className="flex flex-col gap-0.5 pb-1">
              <span className="type-ui-sm text-foreground font-medium">{SENT_LABEL[kind]}</span>
              <span className="type-ui-xs text-foreground-secondary">{SENT_DESCRIPTION[kind]}</span>
            </div>
            {transcript && (
              <FadingScrollable
                className={twMerge(scrollableAreaClassName, 'type-ui-xs pb-4')}
                onClick={(e) => e.stopPropagation()}
                fadeSize="20%"
              >
                {transcript}
              </FadingScrollable>
            )}
          </div>
        }
      >
        {pill}
      </PreviewableContext>
    )
  },
)
SentLiveTranscript.displayName = 'SentLiveTranscript'
